import React from 'react';
import { X } from 'lucide-react';
import { swatchName } from '../elements/render/colorSwatch';
import type { Commerce } from './ShopCommerce';
import type { PendingLine } from './pendingLine';
import type { StorefrontProduct } from '../api/client';
import { cartMoney } from './cartMoney';
import { QuantityStepper } from '../elements/render/PurchaseControls';
import './cartLines.css';
import { maxQuantity } from './variantStock';
import { responsive, THUMB_SIZES } from '../elements/render/responsiveImage';
import { named, shopNames, useShopNames } from './shopNames';
import { shopWords, withCount } from './shopWords';

// The cart's lines as Sleek draws them: a picture, the name linking back to the product, the option chosen
// and the price. In the drawer each line also has a stepper and a remove button; on a checkout page the
// lines are the order's summary, so they show the quantity and the line's total and change nothing — a
// quantity changed under a half-filled form would re-price it out from under the shopper.

type CartLinesProps = { commerce: Commerce; editable: boolean };
type CartLineProps = CartLinesProps & { line: PendingLine };

// Reads the name of the option a line holds, or nothing for a product that has only its default one.
const optionName = (title: string | undefined): string => (title && !/^default/i.test(title) ? title : '');

/** What one line shows, read off the product behind it and the server's price for it, and what a quantity break saved. */
type LineView = { product?: StorefrontProduct; inStock: number; title: string; href: string; option: string; unit: number; list: number; saving: number; picture?: string };

// Works out what one line shows: its product, option, unit price, stock and where it links.
function lineView(commerce: Commerce, line: PendingLine): LineView {
  const product = commerce.products.find((row) => row.slug === line.slug);
  const variant = product?.variants.find((row) => row.id === line.variantId);
  const priced = commerce.quote?.lines.find((row) => row.variantId === line.variantId);
  const unit = Number(priced?.unitPrice ?? variant?.price ?? 0);
  return {
    product, title: product?.title ?? line.slug, href: `/products/${encodeURIComponent(line.slug)}`, option: optionName(variant?.title),
    unit, list: Number(priced?.listUnitPrice ?? unit), saving: Number(priced?.discountAmount ?? 0),
    inStock: variant ? maxQuantity(variant) : line.quantity, picture: product?.images[0]?.src
  };
}

// Draws a line's remove button and its stepper, which may go no higher than the stock behind it.
const LineControls: React.FC<{ commerce: Commerce; line: PendingLine; view: LineView }> = ({ commerce, line, view }) => (
  <div className="xe-cart-line-controls">
    <button type="button" className="xe-cart-line-remove" aria-label={named(shopNames(commerce.language).remove, { title: view.title })} onClick={() => commerce.remove(line.variantId)}><X size={18} aria-hidden="true" /></button>
    <QuantityStepper value={line.quantity} max={view.inStock} disabled={!view.product} name={view.title} onChange={(quantity) => commerce.setQuantity(line.variantId, quantity)} />
  </div>
);

// Draws a price, with the price before a quantity break struck through beside it when one lowered it.
const LinePrice: React.FC<{ now: string; was: string | null }> = ({ now, was }) => (
  <span className="xe-cart-line-price">{was ? <s className="xe-cart-line-was">{was}</s> : null}{now}</span>
);

// Draws one line: picture, name, option and price, with its controls when the cart can be changed here.
const CartLine: React.FC<CartLineProps> = ({ commerce, editable, line }) => {
  const view = lineView(commerce, line);
  // Spells an amount in the cart's currency and reading direction.
  const money = (amount: number) => cartMoney(amount, commerce.currency, commerce.direction);
  const words = shopWords(commerce.language);
  const detail = [view.option ? swatchName(view.option) : '', editable ? '' : withCount(words.lineQuantity, line.quantity)].filter(Boolean).join(' · ');
  const discounted = view.list > view.unit;
  const count = editable ? 1 : line.quantity;
  return (
    <li className="xe-cart-line">
      <a className="xe-cart-line-media" href={view.href} tabIndex={-1} aria-hidden="true">{view.picture ? <img {...responsive(view.picture, THUMB_SIZES)} alt="" width={96} height={96} loading="lazy" /> : null}</a>
      <div className="xe-cart-line-info">
        <a className="xe-cart-line-title" href={view.href} dir="auto">{view.title}</a>
        {detail ? <span className="xe-cart-line-option">{detail}</span> : null}
        {editable ? <LinePrice now={money(view.unit)} was={discounted ? money(view.list) : null} /> : null}
        {view.saving > 0 ? <span className="xe-cart-line-saving">{words.quantityDiscount} −{money(view.saving)}</span> : null}
      </div>
      {editable ? <LineControls commerce={commerce} line={line} view={view} /> : <LinePrice now={money(view.unit * count)} was={discounted ? money(view.list * count) : null} />}
    </li>
  );
};

// Stands in for the lines while their products are first read, shaped like them so nothing jumps.
const CartLinesSkeleton: React.FC<{ count: number }> = ({ count }) => {
  const names = useShopNames();
  return (
    <ul className="xe-cart-lines" aria-busy="true" aria-label={names.loadingCart}>
      {Array.from({ length: Math.min(count, 4) }, (_unused, index) => (
        <li key={index} className="xe-cart-line xe-cart-line--skeleton">
          <span className="xe-cart-line-media" />
          <span className="xe-cart-line-info"><span className="xe-cart-skel" /><span className="xe-cart-skel xe-cart-skel--short" /></span>
        </li>
      ))}
    </ul>
  );
};

// Lists what the cart holds, or its skeleton while the products behind the lines are first read.
export const CartLineList: React.FC<CartLinesProps> = ({ commerce, editable }) => {
  if (commerce.loading && commerce.products.length === 0) return <CartLinesSkeleton count={commerce.lines.length} />;
  return (
    <ul className="xe-cart-lines" data-summary={editable ? undefined : ''} aria-busy={commerce.loading}>
      {commerce.lines.map((line) => <CartLine key={line.variantId} commerce={commerce} editable={editable} line={line} />)}
    </ul>
  );
};
